import React, { useState } from 'react';
import { X, Tag, Plus, Check, Search, Sparkles } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { toast } from 'sonner';

interface TagsModalProps {
  isOpen: boolean;
  onClose: () => void;
  currentTags: string[];
  onSaveTags: (tags: string[]) => void;
  wellName?: string;
}

// Predefined tag categories with colors
const PREDEFINED_TAGS = {
  'Priority': [
    { name: 'High Priority', color: 'bg-red-100 text-red-800 border-red-300' },
    { name: 'Medium Priority', color: 'bg-orange-100 text-orange-800 border-orange-300' },
    { name: 'Low Priority', color: 'bg-yellow-100 text-yellow-800 border-yellow-300' },
  ],
  'Status': [
    { name: 'Productive', color: 'bg-green-100 text-green-800 border-green-300' },
    { name: 'New Well', color: 'bg-blue-100 text-blue-800 border-blue-300' },
    { name: 'Needs Inspection', color: 'bg-purple-100 text-purple-800 border-purple-300' },
    { name: 'Under Maintenance', color: 'bg-gray-100 text-gray-800 border-gray-300' },
  ],
  'Performance': [
    { name: 'Top Performer', color: 'bg-emerald-100 text-emerald-800 border-emerald-300' },
    { name: 'Low Pressure', color: 'bg-pink-100 text-pink-800 border-pink-300' },
    { name: 'High Water Cut', color: 'bg-cyan-100 text-cyan-800 border-cyan-300' },
    { name: 'Declining', color: 'bg-amber-100 text-amber-800 border-amber-300' },
  ],
  'Operational': [
    { name: 'Remote Location', color: 'bg-indigo-100 text-indigo-800 border-indigo-300' },
    { name: 'Easy Access', color: 'bg-teal-100 text-teal-800 border-teal-300' },
    { name: 'Winter Access Only', color: 'bg-sky-100 text-sky-800 border-sky-300' },
    { name: 'Watch List', color: 'bg-rose-100 text-rose-800 border-rose-300' },
  ],
};

export default function TagsModal({
  isOpen,
  onClose,
  currentTags,
  onSaveTags,
  wellName = 'this well',
}: TagsModalProps) {
  const [selectedTags, setSelectedTags] = useState<string[]>(currentTags);
  const [customTag, setCustomTag] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [showCustomInput, setShowCustomInput] = useState(false);

  if (!isOpen) return null;

  const toggleTag = (tagName: string) => {
    if (selectedTags.includes(tagName)) {
      setSelectedTags(selectedTags.filter((t) => t !== tagName));
    } else {
      if (selectedTags.length >= 10) {
        toast.error('Maximum 10 tags allowed per well');
        return;
      }
      setSelectedTags([...selectedTags, tagName]);
    }
  };

  const addCustomTag = () => {
    const trimmed = customTag.trim();
    if (!trimmed) {
      toast.error('Please enter a tag name');
      return;
    }
    if (selectedTags.includes(trimmed)) {
      toast.error('This tag is already added');
      return;
    }
    if (selectedTags.length >= 10) {
      toast.error('Maximum 10 tags allowed per well');
      return;
    }
    setSelectedTags([...selectedTags, trimmed]);
    setCustomTag('');
    setShowCustomInput(false);
    toast.success('Custom tag added');
  };

  const removeTag = (tagName: string) => {
    setSelectedTags(selectedTags.filter((t) => t !== tagName));
  };

  const handleSave = () => {
    onSaveTags(selectedTags);
    toast.success(`${selectedTags.length} tag${selectedTags.length !== 1 ? 's' : ''} saved`);
    onClose();
  };

  const getTagColor = (tagName: string) => {
    for (const category of Object.values(PREDEFINED_TAGS)) {
      const tag = category.find((t) => t.name === tagName);
      if (tag) return tag.color;
    }
    return 'bg-purple-100 text-purple-800 border-purple-300'; // Default for custom tags
  };

  const isTagSelected = (tagName: string) => selectedTags.includes(tagName);

  // Filter tags based on search
  const filteredCategories = Object.entries(PREDEFINED_TAGS).reduce((acc, [category, tags]) => {
    const filteredTags = tags.filter((tag) =>
      tag.name.toLowerCase().includes(searchQuery.toLowerCase())
    );
    if (filteredTags.length > 0) {
      acc[category] = filteredTags;
    }
    return acc;
  }, {} as typeof PREDEFINED_TAGS);

  return (
    <div className="fixed inset-0 z-50 flex items-end md:items-center md:justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative bg-white w-full md:max-w-lg md:rounded-2xl rounded-t-3xl shadow-2xl flex flex-col max-h-[90vh] animate-slide-up">
        {/* Header */}
        <div className="bg-gradient-to-r from-purple-600 to-purple-700 text-white p-4 rounded-t-3xl md:rounded-t-2xl flex-shrink-0">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center space-x-2">
              <Tag className="h-6 w-6" />
              <h2 className="text-xl font-bold">Manage Tags</h2>
            </div>
            <button
              onClick={onClose}
              className="p-2 rounded-full hover:bg-white/20 active:bg-white/30 transition-colors"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
          <p className="text-sm text-purple-100">
            Add or remove tags for {wellName}
          </p>
        </div>

        {/* Selected Tags Display */}
        {selectedTags.length > 0 && (
          <div className="bg-purple-50 p-4 border-b flex-shrink-0">
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm font-semibold text-purple-900">
                Selected Tags ({selectedTags.length}/10)
              </p>
              {selectedTags.length > 0 && (
                <button
                  onClick={() => setSelectedTags([])}
                  className="text-xs text-purple-600 hover:text-purple-800 font-medium"
                >
                  Clear All
                </button>
              )}
            </div>
            <div className="flex flex-wrap gap-2">
              {selectedTags.map((tag) => (
                <Badge
                  key={tag}
                  className={`${getTagColor(tag)} border px-3 py-1.5 flex items-center space-x-1`}
                >
                  <span>{tag}</span>
                  <button
                    onClick={() => removeTag(tag)}
                    className="ml-1 hover:opacity-70"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </Badge>
              ))}
            </div>
          </div>
        )}

        {/* Search Bar */}
        <div className="p-4 border-b flex-shrink-0">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input
              type="text"
              placeholder="Search tags..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 h-11"
            />
          </div>
        </div>

        {/* Tags Content */}
        <div className="flex-1 overflow-y-auto p-4 space-y-6">
          {/* Predefined Tags by Category */}
          {Object.keys(filteredCategories).length > 0 ? (
            Object.entries(filteredCategories).map(([category, tags]) => (
              <div key={category}>
                <div className="flex items-center space-x-2 mb-3">
                  <Sparkles className="h-4 w-4 text-purple-600" />
                  <h3 className="font-semibold text-gray-900">{category}</h3>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  {tags.map((tag) => {
                    const selected = isTagSelected(tag.name);
                    return (
                      <button
                        key={tag.name}
                        onClick={() => toggleTag(tag.name)}
                        className={`
                          ${tag.color} border-2 rounded-lg p-3 text-left
                          transition-all relative
                          ${selected 
                            ? 'ring-2 ring-purple-600 ring-offset-2 scale-95' 
                            : 'hover:scale-105 active:scale-95'
                          }
                        `}
                      >
                        <div className="flex items-start justify-between">
                          <span className="text-sm font-medium pr-2">{tag.name}</span>
                          {selected && (
                            <div className="flex-shrink-0 w-5 h-5 bg-purple-600 rounded-full flex items-center justify-center">
                              <Check className="h-3 w-3 text-white" />
                            </div>
                          )}
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            ))
          ) : (
            <div className="text-center py-8">
              <Search className="h-12 w-12 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500">No tags found matching "{searchQuery}"</p>
            </div>
          )}

          {/* Custom Tags Section */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center space-x-2">
                <Plus className="h-4 w-4 text-purple-600" />
                <h3 className="font-semibold text-gray-900">Custom Tags</h3>
              </div>
              {!showCustomInput && (
                <button
                  onClick={() => setShowCustomInput(true)}
                  className="text-sm text-purple-600 hover:text-purple-800 font-medium"
                >
                  + Add Custom
                </button>
              )}
            </div>

            {showCustomInput && (
              <div className="mb-3 space-y-2">
                <Input
                  type="text"
                  placeholder="Enter custom tag name..."
                  value={customTag}
                  onChange={(e) => setCustomTag(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && addCustomTag()}
                  className="h-11"
                  autoFocus
                />
                <div className="flex space-x-2">
                  <Button
                    onClick={addCustomTag}
                    size="sm"
                    className="flex-1 bg-purple-600 hover:bg-purple-700"
                  >
                    <Plus className="h-4 w-4 mr-1" />
                    Add Tag
                  </Button>
                  <Button
                    onClick={() => {
                      setShowCustomInput(false);
                      setCustomTag('');
                    }}
                    size="sm"
                    variant="outline"
                    className="flex-1"
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            )}

            {/* Show custom tags that aren't in predefined list */}
            {selectedTags.filter((tag) => {
              return !Object.values(PREDEFINED_TAGS)
                .flat()
                .some((t) => t.name === tag);
            }).length > 0 && (
              <div className="flex flex-wrap gap-2">
                {selectedTags
                  .filter((tag) => {
                    return !Object.values(PREDEFINED_TAGS)
                      .flat()
                      .some((t) => t.name === tag);
                  })
                  .map((tag) => (
                    <Badge
                      key={tag}
                      className="bg-purple-100 text-purple-800 border-2 border-purple-300 px-3 py-1.5 flex items-center space-x-1"
                    >
                      <Sparkles className="h-3 w-3" />
                      <span>{tag}</span>
                      <button
                        onClick={() => removeTag(tag)}
                        className="ml-1 hover:opacity-70"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </Badge>
                  ))}
              </div>
            )}
          </div>
        </div>

        {/* Footer Actions */}
        <div className="sticky bottom-0 bg-white border-t p-4 flex space-x-3 flex-shrink-0">
          <Button
            variant="outline"
            onClick={onClose}
            className="flex-1 h-12"
          >
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            className="flex-1 h-12 bg-purple-600 hover:bg-purple-700"
          >
            <Check className="h-5 w-5 mr-2" />
            Save Tags ({selectedTags.length})
          </Button>
        </div>
      </div>
    </div>
  );
}
